import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AcUnit,
  ArrowBack,
  DeleteForever,
  LockOpen,
  Refresh,
  Savings,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  cardAction,
  fetchCardDetail,
  fetchCardQuote,
  fetchCardTransactions,
  fetchCardWallet,
  terminateCard,
} from '../hooks/hooks';

const money = (v, dp = 2) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

const STATUS_COLOR = {
  ACTIVE: 'success', CREATING: 'warning', PROCESSING: 'warning',
  FROZEN: 'info', SUSPENDED: 'warning', TERMINATED: 'default', FAILED: 'error',
};

const readError = (err, fallback) =>
  err?.response?.data?.message
  || (err?.response?.data?.errors && Object.values(err.response.data.errors).flat()[0])
  || fallback;

const CardDetail = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const [feedback, setFeedback] = useState(null);
  const [fundOpen, setFundOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['card', cardId],
    queryFn: () => fetchCardDetail(cardId),
    refetchOnWindowFocus: false,
  });

  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: ['cardTransactions', cardId],
    queryFn: () => fetchCardTransactions(cardId),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const card = data?.card;
  const transactions = txData?.transactions || [];

  const actionMutation = useMutation({
    mutationFn: cardAction,
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      setFeedback({ severity: 'success', message: res?.message || `Card ${vars.action}d.` });
    },
    onError: (err) => setFeedback({ severity: 'error', message: readError(err, 'Action failed.') }),
  });

  if (isLoading) {
    return <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography color="text.secondary">Loading card...</Typography>
    </Container>;
  }

  if (error || !card) {
    return <Container maxWidth="md" sx={{ py: 4 }}>
      <Alert severity="error">Card not found.</Alert>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/cards')} sx={{ mt: 2 }}>
        Back to cards
      </Button>
    </Container>;
  }

  const terminated = card.status === 'TERMINATED';

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate('/cards')} size="small"><ArrowBack /></IconButton>
          <Typography variant="h6" fontWeight={700}>
            {card.masked_number || (card.last4 ? `•••• ${card.last4}` : 'Card')}
          </Typography>
          <Chip label={card.status} size="small" color={STATUS_COLOR[card.status] || 'default'} />
        </Stack>
        <Tooltip title="Refresh"><IconButton onClick={() => refetch()}><Refresh /></IconButton></Tooltip>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2, borderRadius: '10px' }}
               onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      {['CREATING', 'PROCESSING'].includes(card.status) && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
          This card is still being issued. The number and expiry appear once it is ready.
        </Alert>
      )}
      {card.status === 'FAILED' && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          Issuing this card failed and the amount was returned to your card balance.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <MuiCard elevation={0} sx={{ borderRadius: '14px', color: '#fff',
                    background: 'linear-gradient(140deg, #0c1a14 0%, #092a1e 55%, #0d5c40 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 1, mb: 3 }}>
                {card.brand || 'VIRTUAL CARD'}
              </Typography>
              <Typography fontSize={20} fontFamily="monospace" letterSpacing={2} mb={3}>
                {card.masked_number || (card.last4 ? `•••• •••• •••• ${card.last4}` : '•••• •••• •••• ••••')}
              </Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                <Box>
                  <Typography fontSize={10} sx={{ color: 'rgba(255,255,255,0.5)' }}>CARDHOLDER</Typography>
                  <Typography fontSize={14}>{card.name_on_card || '—'}</Typography>
                </Box>
                <Box textAlign="right">
                  <Typography fontSize={10} sx={{ color: 'rgba(255,255,255,0.5)' }}>EXPIRES</Typography>
                  <Typography fontSize={14}>{card.expiry_date || '––/––'}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </MuiCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <MuiCard elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg,
                                       borderRadius: '12px', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Card balance</Typography>
              <Typography variant="h4" fontWeight={800} color="#0BA480" mb={2}>
                ${money(card.balance)}
              </Typography>

              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="contained" startIcon={<Savings />}
                  onClick={() => setFundOpen(true)}
                  disabled={terminated || !card.is_reloadable}
                  sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12,
                        fontWeight: 600, boxShadow: 0, backgroundColor: '#0BA480',
                        '&:hover': { boxShadow: 'none' } }}
                >
                  Add Funds
                </Button>

                {card.status === 'ACTIVE' && (
                  <Button variant="outlined" startIcon={<AcUnit />}
                          disabled={actionMutation.isPending}
                          onClick={() => actionMutation.mutate({ cardId, action: 'freeze' })}
                          sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}>
                    Freeze
                  </Button>
                )}
                {card.status === 'FROZEN' && (
                  <Button variant="outlined" startIcon={<LockOpen />}
                          disabled={actionMutation.isPending}
                          onClick={() => actionMutation.mutate({ cardId, action: 'unfreeze' })}
                          sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}>
                    Unfreeze
                  </Button>
                )}

                <Button variant="outlined" color="error" startIcon={<DeleteForever />}
                        disabled={terminated}
                        onClick={() => setTerminateOpen(true)}
                        sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}>
                  Terminate
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Stack gap={0.75}>
                {[
                  ['Card ID', card.card_id],
                  ['Currency', card.currency],
                  ['Reloadable', card.is_reloadable ? 'Yes' : 'No'],
                  ['Opened', card.created_at ? format(new Date(card.created_at), 'd MMM yyyy') : '—'],
                ].map(([k, v]) => (
                  <Stack key={k} direction="row" justifyContent="space-between">
                    <Typography fontSize={12.5} color="text.secondary">{k}</Typography>
                    <Typography fontSize={12.5} fontFamily={k === 'Card ID' ? 'monospace' : undefined}>
                      {v}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </MuiCard>
        </Grid>
      </Grid>

      <MuiCard elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>Transactions</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Merchant</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txLoading ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Loading transactions...</Typography>
                </TableCell></TableRow>
              ) : transactions.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No transactions yet</Typography>
                </TableCell></TableRow>
              ) : transactions.map((t, i) => (
                <TableRow key={t.id || i}>
                  <TableCell>
                    <Typography fontSize={12.5}>
                      {t.createdAt ? format(new Date(t.createdAt), 'MMM d, HH:mm') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography fontSize={13}>{t.merchantName || t.description || '—'}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography fontSize={13} fontWeight={600}>
                      {t.currency || '$'}{money(t.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t.status || '—'} size="small" sx={{ fontSize: 11 }}
                          color={t.status === 'APPROVED' ? 'success'
                                 : t.status === 'DECLINED' ? 'error' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MuiCard>

      <FundDialog open={fundOpen} onClose={() => setFundOpen(false)} cardId={cardId}
                  onResult={setFeedback} />
      <TerminateDialog open={terminateOpen} onClose={() => setTerminateOpen(false)} cardId={cardId}
                      balance={card.balance} onResult={setFeedback}
                      onDone={() => navigate('/cards')} />
    </Container>
  );
};

const FundDialog = ({ open, onClose, cardId, onResult }) => {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setAmount(''); setPin(''); setError(null); } }, [open]);

  const { data: wallet } = useQuery({
    queryKey: ['cardWallet'], queryFn: fetchCardWallet, enabled: open,
  });
  const { data: quote } = useQuery({
    queryKey: ['cardFundQuote', amount],
    queryFn: () => fetchCardQuote({ amount, fund: 1 }),
    enabled: open && Number(amount) > 0,
    retry: false,
  });

  const balance = Number(wallet?.card_balance_usd || 0);
  const total = Number(quote?.total_usd || 0);
  const short = total > 0 && total > balance;

  const mutation = useMutation({
    mutationFn: cardAction,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['cardWallet'] });
      onClose();
      onResult({ severity: 'success', message: res?.message || 'Top-up requested.' });
    },
    onError: (err) => setError(readError(err, 'Top-up failed.')),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add Funds to Card</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>{error}</Alert>}
        <Typography fontSize={13} color="text.secondary" mb={1}>
          Card balance available: ${money(balance)}
        </Typography>
        <TextField size="small" margin="normal" fullWidth type="number" label="Amount (USD)"
                   value={amount} onChange={(e) => setAmount(e.target.value)}
                   helperText="Minimum $5" />
        {quote && (
          <Stack gap={0.5} sx={{ my: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={13} color="text.secondary">Onto the card</Typography>
              <Typography fontSize={13}>${money(quote.card_balance_usd)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={13} color="text.secondary">Funding fee</Typography>
              <Typography fontSize={13}>${money(quote.funding_fee_usd)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={13} fontWeight={600}>Total</Typography>
              <Typography fontSize={14} fontWeight={700} color={short ? '#D32F2F' : '#0BA480'}>
                ${money(quote.total_usd)}
              </Typography>
            </Stack>
            {short && <Typography fontSize={12} color="error">
              Not enough card balance — add ${money(total - balance)} more.
            </Typography>}
          </Stack>
        )}
        <TextField size="small" margin="normal" fullWidth type="password" label="PIN"
                   value={pin} onChange={(e) => setPin(e.target.value)}
                   inputProps={{ maxLength: 4, inputMode: 'numeric' }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: 12, textTransform: 'uppercase' }}>Cancel</Button>
        <Button variant="contained"
                disabled={mutation.isPending || !(Number(amount) >= 5) || short || !/^\d{4}$/.test(pin)}
                onClick={() => { setError(null); mutation.mutate({
                  cardId, action: 'fund', payload: { amount_usd: Number(amount), pin } }); }}
                sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12,
                      fontWeight: 600, boxShadow: 0, backgroundColor: '#0BA480',
                      '&:hover': { boxShadow: 'none' } }}>
          {mutation.isPending ? 'Sending...' : 'Add Funds'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TerminateDialog = ({ open, onClose, cardId, balance, onResult, onDone }) => {
  const queryClient = useQueryClient();
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setPin(''); setError(null); } }, [open]);

  const mutation = useMutation({
    mutationFn: terminateCard,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      queryClient.invalidateQueries({ queryKey: ['cardWallet'] });
      onClose();
      onResult({
        severity: 'success',
        message: `Card terminated. $${money(res?.refunded_usd)} returned to your card balance.`,
      });
      onDone?.();
    },
    onError: (err) => setError(readError(err, 'Could not terminate the card.')),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Terminate Card</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>{error}</Alert>}
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
          This cannot be undone. The card stops working immediately and its
          ${money(balance)} balance returns to your card balance.
        </Alert>
        <TextField size="small" margin="normal" fullWidth type="password" label="PIN"
                   value={pin} onChange={(e) => setPin(e.target.value)}
                   inputProps={{ maxLength: 4, inputMode: 'numeric' }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: 12, textTransform: 'uppercase' }}>Cancel</Button>
        <Button variant="contained" color="error"
                disabled={mutation.isPending || !/^\d{4}$/.test(pin)}
                onClick={() => { setError(null); mutation.mutate({ cardId, pin }); }}
                sx={{ borderRadius: '3px', textTransform: 'uppercase', fontSize: 12,
                      fontWeight: 600, boxShadow: 0 }}>
          {mutation.isPending ? 'Terminating...' : 'Terminate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardDetail;
